import React, { useEffect, useMemo, useState } from 'react';
import { getAnalyticsSummary, getAnalyticsTimeseries, getAnalyticsSources } from '../utils/api';
import { track } from '../utils/analytics';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, LineElement, PointElement, LinearScale, Title, Tooltip, Legend, CategoryScale,
} from 'chart.js';

ChartJS.register(LineElement, PointElement, LinearScale, Title, Tooltip, Legend, CategoryScale);

export default function AdminAnalyticsPage() {
  const [range, setRange] = useState(30);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const k = (key) => (summary?.kpis?.[key] ?? 0);
  const [signups, setSignups] = useState([]);
  const [visits, setVisits] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [tier, setTier] = useState([]); // [{date, core, free}]
  const [sources, setSources] = useState({ utm_source: [], referrers: [] });
  const [error, setError] = useState(null);

  useEffect(() => { track('admin_analytics_view', { path: '/admin/analytics' }); }, []);

  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token') || localStorage.getItem('clairifi_token') || '';
      try {
        const [sum, s90, v90, sess, upl, tr, src] = await Promise.all([
          getAnalyticsSummary({}, token),
          getAnalyticsTimeseries('signups', range, token),
          getAnalyticsTimeseries('visits', range, token),
          getAnalyticsTimeseries('sessions', range, token),
          getAnalyticsTimeseries('uploads', range, token),
          fetch('/api/admin/analytics/tier-activity?days=' + range, { headers: token ? { Authorization: `Bearer ${token}` } : {} }).then(r => r.json()),
          getAnalyticsSources(range, token),
        ]);

        if (dead) return;
        setSummary(sum || null);
        setSignups(s90 || []);
        setVisits(v90 || []);
        setSessions(sess || []);
        setUploads(upl); 
        setTier(tr);
        setSources(src || { utm_source: [], referrers: [] });
      } catch (e) {
        if (!dead) setError(e?.message || 'Failed to load analytics');
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, [range]);

  const chartData = (rows, label) => ({
    labels: rows.map(r => r.date),
    datasets: [{
      label,
      data: rows.map(r => r.value),
      tension: 0.25,
      fill: false,
      spanGaps: true
    }]
  });

  return (
    <div className="analytics-wrap">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 className="page-title" style={{ marginBottom: 0 }}>Analytics</h1>
        <select value={range} onChange={(e) => setRange(Number(e.target.value))} style={{ padding: 6, borderRadius: 6 }}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        {/* KPI header */}
        <section className="analytics-kpis">
          <div className="kpi"><div className="kpi-label">DAU</div><div className="kpi-value">{k('dau')}</div></div>
          <div className="kpi"><div className="kpi-label">WAU</div><div className="kpi-value">{k('wau')}</div></div>
          <div className="kpi"><div className="kpi-label">MAU</div><div className="kpi-value">{k('mau')}</div></div>
          <div className="kpi"><div className="kpi-label">Signups</div><div className="kpi-value">{k('new_users')}</div></div>
          <div className="kpi"><div className="kpi-label">Uploads</div><div className="kpi-value">{k('uploads')}</div></div>
          <div className="kpi"><div className="kpi-label">Active Subs</div><div className="kpi-value">{k('active_subs')}</div></div>
        </section>

      </div>

      {error && (
        <div
          className="analytics-error"
          style={{
            margin: '12px 0',
            padding: '10px 12px',
            borderRadius: 8,
            background: '#fff3f3',
            color: '#b91c1c',
            border: '1px solid #fecaca'
          }}
        >
          {error.includes('Admins only')
            ? 'Access restricted: Admins only. Please sign in with an admin account.'
            : error}
        </div>
      )}

      <section className="analytics-kpis">
        {['visits','new_users','dau','mau','uploads','paid_core','trial_free'].map((k) => (
          <div key={k} className="analytics-card">
            <h3 style={{ textTransform: 'capitalize' }}>{k.replace('_',' ')}</h3>
            <div className="big">{loading ? '…' : (summary?.kpis?.[k] ?? '—')}</div>
          </div>
        ))}
      </section>

      <section className="analytics-grid">
        <div className="analytics-card">
          <h3>Signups</h3>
          <Line data={chartData(signups, 'Signups')} options={{ responsive: true, plugins: { legend: { display: false } } }} />
        </div>
        <div className="analytics-card">
          <h3>Visits</h3>
          <Line data={chartData(visits, 'Visits')} options={{ responsive: true, plugins: { legend: { display: false } } }} />
        </div>
        <div className="analytics-card">
          <h3>Sessions (unique users / sessions)</h3>
          <Line data={chartData(sessions, 'Sessions')} options={{ responsive: true, plugins: { legend: { display: false } } }} />
        </div>
      </section>
      <div className="analytics-card">
        <h3>Uploads</h3>
        <Line data={chartData(uploads, 'Uploads')} options={{ responsive: true, plugins: { legend: { display: false } } }} />
      </div>

      <div className="analytics-card">
        <h3>Active users by plan (Core vs Free)</h3>
        <Line data={{
          labels: tier.map(r => r.date),
          datasets: [
            { label: 'Core', data: tier.map(r => r.core), tension: 0.25, fill: false, spanGaps: true },
            { label: 'Free', data: tier.map(r => r.free), tension: 0.25, fill: false, spanGaps: true }
          ]
        }} options={{ responsive: true }} />
      </div>

      <section className="analytics-section">
        <div className="analytics-card">
          <h3>Top sources (utm_source)</h3>
          <table className="analytics-table">
            <thead><tr><th>Source</th><th>Visits</th></tr></thead>
            <tbody>
              {(sources.utm_source || []).map((r) => (
                <tr key={r.name}><td>{r.name}</td><td>{r.count}</td></tr>
              ))}
              {!loading && (!sources.utm_source || sources.utm_source.length === 0) && (
                <tr><td colSpan={2} style={{ opacity: 0.7 }}>No source data</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="analytics-card">
          <h3>Top referrers</h3>
          <table className="analytics-table">
            <thead><tr><th>Domain</th><th>Visits</th></tr></thead>
            <tbody>
              {(sources.referrers || []).map((r) => (
                <tr key={r.domain}><td>{r.domain}</td><td>{r.count}</td></tr>
              ))}
              {!loading && (!sources.referrers || sources.referrers.length === 0) && (
                <tr><td colSpan={2} style={{ opacity: 0.7 }}>No referrers</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
